import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import _ from "lodash";
import TableComponent from "components/Table";
import DatePicker from "components/actions/DatePicker";

import { useTranslation } from "react-i18next";
import PageWrapper from "components/wrappers/PageWrapper";
import { dateRangeUriEncode, getI18nValue } from "utils/Utils";
import { HeaderServiceTitle, pickPrivacyStatus, pickServiceStatus } from ".";
import CustomersService from "api/services/CustomersService";
import axiosClient from "api/shared/AxiosClient";
import moment from "moment";
import PropTypes from 'prop-types';

function UserIdHeader() {
  const { t } = useTranslation();
  return (
    <div className="tooltip tooltip-primary tooltip-right transition duration-150 ease-in-out"
      data-tip={t('customers.customerId.title')}>
      {t('form.identifier_id')}
    </div>
  );
}
function UserServicesHeader() {
  return (
    <div className="flex">
      <HeaderServiceTitle titleKey='horaire_tarif' />
      <HeaderServiceTitle titleKey='cancel_exchange' />
      <HeaderServiceTitle titleKey='train_auto' />
      <HeaderServiceTitle titleKey='reclamation' />
      <HeaderServiceTitle titleKey='astuce' />
      <HeaderServiceTitle titleKey='gare_proche' />
      <HeaderServiceTitle titleKey='tarif_reduit' />
    </div>
  );
}
function UserIdCell({ value }) {
  return <div className="text-xs">{value}</div>;
}
function PrivacyStatusCell_({ value }) {
  return <span>{pickPrivacyStatus(value)}</span>;
}
function UserServicesCell({ value, data, row }) {
  return (
    <div className="text-xs">
      <span>{pickServiceStatus(data[row.id]?.schedulesAndPrices)}</span>
      <span>{pickServiceStatus(value?.cancelAndExchange)}</span>
      <span>{pickServiceStatus(value?.trainAndCar)}</span>
      <span>{pickServiceStatus(value?.claimAndSupport)}</span>
      <span>{pickServiceStatus(value?.travelAndTips)}</span>
      <span>{pickServiceStatus(value?.nearestStation)}</span>
      <span>{pickServiceStatus(value?.discountCard)}</span>
    </div>
  );
}

UserIdCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
PrivacyStatusCell_.propTypes = {
    value: PropTypes.any,
};
UserServicesCell.propTypes = {
    value: PropTypes.shape({
        cancelAndExchange: PropTypes.any,
        trainAndCar: PropTypes.any,
        claimAndSupport: PropTypes.any,
        travelAndTips: PropTypes.any,
        nearestStation: PropTypes.any,
        discountCard: PropTypes.any,
    }),
    data: PropTypes.array,
    row: PropTypes.shape({ id: PropTypes.string }),
};

function CustomerList_() {
  const { t } = useTranslation()
  const [pageCount, setPageCount] = useState(0);
  const [data, setData] = useState([]);
  const [overview, setOverview] = useState({});
  const fetchIdRef = useRef(0);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDateRanges, setSelectedDateRanges] = useState(
    {
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date(),
      key: 'selection',
    }
  );

  const fetchAPIData = async ({ selectedDateRanges, limit, skip, search }) => {
    try {
      setLoading(true);

      const response = await CustomersService.getCustomerListWithPagination(selectedDateRanges, limit, skip, search)

      setData(response.data);

      setPageCount(response.paging.pages);

      setOverview(response.overview)
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  };

  const fetchData = useCallback(
    ({ pageSize, pageIndex }) => {
      const fetchId = ++fetchIdRef.current;
      setLoading(true);
      if (fetchId === fetchIdRef.current) {
        fetchAPIData({
          selectedDateRanges: selectedDateRanges,
          limit: pageSize,
          skip: pageSize * pageIndex,
          search: searchTerm,
        });
      }
    },
    [searchTerm, selectedDateRanges]
  );

  const _handleSearch = _.debounce(
    (search) => {
      setSearchTerm(search);
    },
    1500,
    {
      maxWait: 1500,
    }
  );


  const columns = useMemo(() => [
    {
      Header: UserIdHeader,
      accessor: "userId",
      Cell: UserIdCell,
      show: true,
    },
    {
      Header: t('form.first_name'),
      accessor: "userDetails.firstName",
      Cell: UserIdCell,
      show: true,
    },
    {
      Header: t('form.last_name'),
      accessor: "userDetails.lastName",
      Cell: UserIdCell,
      show: true,
    },
    {
      Header: t('form.email'),
      accessor: "userDetails.email",
      Cell: UserIdCell,
      show: true,
    },
    {
      Header: t('form.privacy_policy'),
      accessor: "userPreferences.isPrivacyPolicyAccepted",
      Cell: PrivacyStatusCell_,
    },
    {
      Header: UserServicesHeader,
      accessor: "userRequests",
      Cell: UserServicesCell,
    },

  ], [t]);

  return (
    <div>


      <UserOverview selectedDateRanges={selectedDateRanges} data={overview}/>


      <PageWrapper
        title={t(getI18nValue('customers', "title"))}
        description={t(getI18nValue('customers', "description"))}
        actions={
          <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2 mb-4">
            {/* <ExportCSV csvData={data} disabled={!data} fileName={`customers_${getDateRangeString(selectedDateRanges)}`} /> */}
            <DatePicker callBack={setSelectedDateRanges} />
            <ExportData selectedDateRanges={selectedDateRanges} />
          </div>
        }
        className="border"
      >
        <div className="container mx-auto flex flex-col">
          <div className="mt-3">
            <input
              id="searchTerm"
              name="searchTerm"
              type="text"
              onChange={(e) => _handleSearch(e.target.value)}
              placeholder="Search by Phone/IdentifierId"
              className="appearance-none block w-1/4 float-right px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          <div className="flex justify-center">
            <TableComponent
              pageCount={pageCount}
              fetchData={fetchData}
              columns={columns}
              loading={loading}
              data={data}
            />
          </div>
          <div className="text-xs capitalize flex space-x-2">
            <div> <span className="font-bold">✓</span> : Service {t('actions.consulted')}</div> ,
            <div>  <span className="font-bold">X</span> : Service {t('actions.not_consulted')}</div>
          </div>
        </div>

      </PageWrapper>
    </div>
  );
}

export default CustomerList_;




function ExportData({ selectedDateRanges }) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axiosClient.get(
        `users/export?${dateRangeUriEncode(selectedDateRanges)}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('[ExportData]', e);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      disabled={isExporting}
      onClick={handleExport}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg font-medium transition
        ${isExporting ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
    >
      {isExporting ? (
        <>
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Exporting…
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export
        </>
      )}
    </button>
  );
}






function UserOverview({ selectedDateRanges, data }) {

  const { t } = useTranslation()


  return (
    <div className="flex gap-8 py-4 bg-white border px-4 ">
      {data &&
        <>
          <div >
            <span className="text-sm font-semibold text-gray-800">{t('customers.title')}</span>{' '}<span className="text-sm ">({t('customers.overview.all_time')})</span>
            :{' '}<span className="text-black font-bold"> {data?.totalAllTime}</span>

          </div>
          <div>
            <span className=" text-sm font-semibold text-gray-800 ">{t('customers.title')}</span>{' '}<span className="text-sm ">({moment(selectedDateRanges.startDate).format('DD/MM/YYYY')} - {moment(selectedDateRanges.endDate).format('DD/MM/YYYY')} )</span>
            :{' '}<span className="text-black font-bold"> {data?.totalSelected} </span>
          </div>
        </>
      }
    </div>

  )
}

ExportData.propTypes = {
  selectedDateRanges: PropTypes.object.isRequired,
};

UserOverview.propTypes = {
  selectedDateRanges: PropTypes.object.isRequired,
  data: PropTypes.object,
};
